/**
 * Complete Batch Modal Component
 * Dialog for completing a sterilization batch
 */

import { forkJoin, Observable, of, Subscription } from 'rxjs'
import { map, startWith, switchMap } from 'rxjs/operators'

import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'

import { Batch, BatchNameAutocompleteItem, CompleteBatchRequest, CustomFieldValue, CustomFieldValueMap } from '../../../models/batch.model'
import { BatchCustomFieldValue, EquipmentCustomField } from '../../../models/equipment.model'
import { OEEDataService } from '../../../services/oee-data.service'
import { SchematicService } from '../../../../schematic-builder/services/schematic.service'
import { AbortBatchModalComponent, AbortBatchModalData, AbortBatchModalResult } from '../abort-batch-modal/abort-batch-modal.component'
import { DetailHeaderAction } from '../../../../../modules/shared/detail-page'

export interface CompleteBatchModalData {
    batch: Batch;                  // The pre-selected batch (oldest/FIFO default)
    allBatches?: Batch[];          // All running batches (present when multi-batch)
    equipmentName: string;
    equipmentId: string;
    eventId: string;  // Required: the production event context
    siteId?: string;
    completeBatchText?: string;
    batchRunId?: string;  // If provided, use Process Flow path (completeBatchRun)
    stationId?: string;            // Station context (for station-owned batches)
    stationName?: string;          // Station name for display
    allowsBatchOperators?: boolean;
    restrictBatchEquipment?: boolean;
    suggestedEndTs?: string;
    /** When true, render a stripped-down layout: only the Job Name field plus a compact info strip. Hide time pickers, parts, custom fields, and time entries. start_ts keeps the job's original start; end_ts defaults to now. */
    simplifiedJobComplete?: boolean;
}

@Component({
    selector: 'app-complete-batch-modal',
    templateUrl: './complete-batch-modal.component.html',
    styleUrls: ['./complete-batch-modal.component.scss'],
  standalone: false
})
export class CompleteBatchModalComponent implements OnInit, OnDestroy {

    completeBatchForm!: FormGroup;
    submitting = false;
    elapsedTime = '';

    /** When simplified mode hits an invalid time range, the user must be able
     * to edit start/end without leaving the modal. Toggling this true reveals
     * the datetime pickers inline; it auto-becomes-true once elapsedTime is
     * "Invalid (end before start)" so the user is never stuck. */
    editTimesExpanded = false;

    /** Currently selected batch (may differ from data.batch when user picks from dropdown) */
    selectedBatch!: Batch;
    /** All running batches for this equipment (length > 1 triggers batch selector) */
    allBatches: Batch[] = [];
    /** Whether to show the batch selection dropdown */
    get showBatchSelector(): boolean { return this.allBatches.length > 1; }

    get completeBatchLabel(): string {
        const customLabel = this.data.completeBatchText?.trim();
        return customLabel || 'Complete Job';
    }

    /** Track whether user has manually edited scrap/reject_parts */
    private scrapTouched = false;
    /** Suppress programmatic valueChanges from triggering touch detection */
    private suppressScrapTouch = false;
    /** Target quantity backing initial values and auto-scrap behavior */
    private resolvedTargetQuantity: number | null = null;

    /** Today's date for pre-filling date fields (time left empty for user to enter) */
    today = new Date();

    /** Start date for pre-filling - uses batch's start date if available, otherwise today */
    get startInitialDate(): Date {
        const batch = this.selectedBatch || this.data.batch;
        return batch.start_ts ? new Date(batch.start_ts) : this.today;
    }

    // Batch name autocomplete
    batchNameItems: BatchNameAutocompleteItem[] = [];
    filteredBatchNames!: Observable<BatchNameAutocompleteItem[]>;
    loading = false;

    // Custom fields
    customFields: EquipmentCustomField[] = [];
    existingFieldValues: BatchCustomFieldValue[] = [];
    loadingCustomFields = false;

    // Whether to show the simplified Complete Job form
    get isSimplifiedComplete(): boolean { return this.data.simplifiedJobComplete === true; }

    get hasInvalidTimeRange(): boolean {
        return this.elapsedTime === 'Invalid (end before start)';
    }

    /** Show the start_ts/end_ts pickers when:
     *   - the modal is in non-simplified (full) mode, OR
     *   - the user explicitly clicked Edit times, OR
     *   - the time range is invalid (the user MUST be able to fix it). */
    get showTimePickers(): boolean {
        return !this.isSimplifiedComplete || this.editTimesExpanded || this.hasInvalidTimeRange;
    }

    toggleEditTimes(): void {
        this.editTimesExpanded = !this.editTimesExpanded;
    }

    // Subscriptions
    private subscriptions: Subscription[] = [];

    constructor(
        private fb: FormBuilder,
        public dialogRef: MatDialogRef<CompleteBatchModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: CompleteBatchModalData,
        private oeeDataService: OEEDataService,
        private schematicService: SchematicService,
        private snackBar: MatSnackBar,
        private dialog: MatDialog
    ) { }

    ngOnInit(): void {
        this.selectedBatch = this.data.batch;
        this.allBatches = this.data.allBatches || [this.data.batch];

        this.initializeForm();
        this.setupTimeChangeListeners();
        this.calculateElapsedTime();
        this.loadBatchNames();
        this.loadCustomFields();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    /**
     * Handle batch selection change from dropdown (multi-batch mode)
     * Re-initializes form and custom fields for the newly selected batch
     */
    onBatchSelected(batchId: string): void {
        const batch = this.allBatches.find(b => b.batch_id === batchId);
        if (!batch || batch.batch_id === this.selectedBatch.batch_id) return;

        this.selectedBatch = batch;
        // Update eventId since batch_id IS the event_id
        this.data.eventId = batch.batch_id;
        // Clear batchRunId — we don't have the run ID for a different batch,
        // so fall back to the dashboard completion path
        this.data.batchRunId = undefined;

        // Reset scrap touch state for new batch
        this.scrapTouched = false;

        // Clean up existing subscriptions before re-init
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];

        // Re-initialize form with new job data
        this.initializeForm();
        this.setupTimeChangeListeners();
        this.calculateElapsedTime();
        this.loadCustomFields();
    }

    /**
     * Initialize form with default values
     */
    private initializeForm(): void {
        // Initialize start_ts with selected batch's start time, end_ts empty (user must fill in for compliance)
        const batch = this.selectedBatch;
        const startTime = batch.start_ts ? new Date(batch.start_ts) : null;
        // The job's target_quantity is the source of truth for the prefill.
        // start_batch() seeds it from product.default_quantity, and any user
        // override flows through it. We deliberately do NOT fall back to
        // batch.good_parts here — legacy batches may have a stale good_parts
        // (e.g. equipment-default seed of 1) that the user later corrected
        // by raising target_quantity. Prefer the user's intent (target).
        this.resolvedTargetQuantity = batch.target_quantity ?? null;
        const initialGoodParts = batch.target_quantity ?? batch.good_parts ?? 0;

        // Default end_ts to NOW so the form is valid out of the box (start_ts
        // comes from batch.start_ts which is in the past). User can override
        // if they want to record a different end time. Avoids the "Invalid
        // (end before start)" trap when the operator opens Complete after
        // already clocking out and the picker defaults to a stale moment.
        const initialEndTs = this.data.suggestedEndTs
            ? new Date(this.data.suggestedEndTs)
            : new Date();

        this.completeBatchForm = this.fb.group({
            start_ts: [startTime, Validators.required],
            end_ts: [initialEndTs, Validators.required],
            batch_number: [batch.batch_number || ''],
            good_parts: [initialGoodParts, [Validators.min(0)]],
            reject_parts: [0, [Validators.min(0)]],
            custom_field_values: this.fb.group({})
        }, { validators: this.timeRangeValidator });

        this.setupAutoScrapCalculation();
    }

    /**
     * Auto-calculate scrap when good_parts changes:
     * scrap = target_quantity - good_parts (unless user has manually edited scrap)
     */
    private setupAutoScrapCalculation(): void {
        // When good_parts changes, auto-fill scrap if not touched
        this.subscriptions.push(
            this.completeBatchForm.get('good_parts')!.valueChanges.subscribe(goodParts => {
                const targetQty = this.resolvedTargetQuantity;
                if (!this.scrapTouched && targetQty !== null) {
                    const scrap = Math.max(0, targetQty - (Number(goodParts) || 0));
                    this.suppressScrapTouch = true;
                    this.completeBatchForm.get('reject_parts')!.setValue(scrap, { emitEvent: true });
                    this.suppressScrapTouch = false;
                }
            })
        );

        // When reject_parts changes by user, mark as touched
        this.subscriptions.push(
            this.completeBatchForm.get('reject_parts')!.valueChanges.subscribe(() => {
                if (!this.suppressScrapTouch) {
                    this.scrapTouched = true;
                }
            })
        );
    }

    /**
     * Check if the form can be submitted
     * Both start and end times are required
     */
    get canSubmit(): boolean {
        if (this.completeBatchForm?.invalid) {
            return false;
        }
        const startTs = this.completeBatchForm?.get('start_ts')?.value;
        const endTs = this.completeBatchForm?.get('end_ts')?.value;
        if (!startTs || !endTs) {
            return false;
        }
        return true;
    }

    /**
     * Validator to ensure end time is strictly after start time (matching backend validation)
     */
    private timeRangeValidator(group: AbstractControl): ValidationErrors | null {
        const startTs = group.get('start_ts')?.value;
        const endTs = group.get('end_ts')?.value;

        if (startTs && endTs) {
            const startDate = new Date(startTs);
            const endDate = new Date(endTs);
            if (endDate <= startDate) {
                return { invalidTimeRange: true };
            }
        }
        return null;
    }

    /**
     * Setup listeners for time field changes to update elapsed time
     */
    private setupTimeChangeListeners(): void {
        const startControl = this.completeBatchForm.get('start_ts');
        const endControl = this.completeBatchForm.get('end_ts');

        if (startControl && endControl) {
            this.subscriptions.push(
                startControl.valueChanges.subscribe(() => this.calculateElapsedTime()),
                endControl.valueChanges.subscribe(() => this.calculateElapsedTime())
            );
        }
    }

    /**
     * Get the equipment ID - prefer explicit data, fall back to batch's equipment_id
     */
    private getEquipmentId(): string {
        return this.data.equipmentId || this.data.batch.equipment_id;
    }

    /**
     * Load custom fields and existing values for THIS event
     * Custom field values are tied to (batch, event, equipment) triple
     */
    private loadCustomFields(): void {
        // Simplified mode hides the custom-fields section entirely, so skip the fetch
        // (and the full-modal loading spinner that comes with it).
        if (this.isSimplifiedComplete) {
            this.customFields = [];
            this.existingFieldValues = [];
            this.loadingCustomFields = false;
            return;
        }

        const equipmentId = this.getEquipmentId();
        const eventId = this.data.eventId;

        if (!eventId) {
            console.warn('CompleteBatchModal: Missing eventId, custom fields disabled');
            this.customFields = [];
            this.existingFieldValues = [];
            this.loadingCustomFields = false;
            return;
        }

        this.loadingCustomFields = true;

        const fields$: Observable<EquipmentCustomField[]> = equipmentId
            ? this.oeeDataService.getEquipmentCustomFields(equipmentId)
            : of([]);

        // Load both custom field definitions and existing batch values in parallel
        forkJoin({
            fields: fields$,
            values: this.oeeDataService.getBatchCustomFieldValues(this.selectedBatch.batch_id, eventId)
        }).subscribe({
            next: ({ fields, values }) => {
                // Filter to show fields configured for complete batch
                this.customFields = fields.filter(f => f.display_in === 'complete');
                this.existingFieldValues = values;
                this.setupCustomFieldControls();
                this.loadingCustomFields = false;
            },
            error: (error) => {
                console.error('Failed to load custom fields:', error);
                this.loadingCustomFields = false;
            }
        });
    }

    /**
     * Setup form controls for custom fields with existing values
     */
    private setupCustomFieldControls(): void {
        const customFieldsGroup = this.completeBatchForm.get('custom_field_values') as FormGroup;

        this.customFields.forEach(field => {
            // Check if there's an existing value for this field
            const existingValue = this.existingFieldValues.find(v => v.custom_field_id === field.id);

            let value: CustomFieldValue;
            if (existingValue) {
                // Use existing value
                value = existingValue.value;
                // Convert string to boolean for checkbox
                if (field.type === 'checkbox') {
                    value = existingValue.value === 'true';
                }
            } else {
                // Use default value
                value = field.default_value || '';
                if (field.type === 'checkbox') {
                    value = field.default_value === 'true';
                }
            }

            // Add required validator if field is marked as required
            const validators = field.is_required ? [Validators.required] : [];
            customFieldsGroup.addControl(field.id, new FormControl(value, validators));
        });
        
        // Update validity after adding controls
        customFieldsGroup.updateValueAndValidity();
        this.completeBatchForm.updateValueAndValidity();
    }

    /**
     * Load batch names for autocomplete suggestions
     */
    private loadBatchNames(): void {
        this.loading = true;
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 90);

        this.oeeDataService.getAllBatchNames(
            this.oeeDataService.toISOString(startDate),
            this.oeeDataService.toISOString(endDate)
        ).subscribe({
            next: (items) => {
                this.batchNameItems = items;
                this.loading = false;
                this.setupAutocomplete();
            },
            error: (error) => {
                console.error('Failed to load batch names:', error);
                this.loading = false;
                this.setupAutocomplete();
            }
        });
    }

    /**
     * Setup autocomplete filtering
     */
    private setupAutocomplete(): void {
        this.filteredBatchNames = this.completeBatchForm.get('batch_number')!.valueChanges.pipe(
            startWith(this.selectedBatch.batch_number || ''),
            map(value => this._filterBatchNames(value || ''))
        );
    }

    /**
     * Filter batch names based on user input
     */
    private _filterBatchNames(value: string): BatchNameAutocompleteItem[] {
        const filterValue = value.toLowerCase();
        return this.batchNameItems.filter(item =>
            item.batch_number.toLowerCase().includes(filterValue)
        );
    }

    /**
     * Format the last used timestamp for display
     */
    formatLastUsed(timestamp: string): string {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    /**
     * Calculate elapsed time between start and end times from form
     * If end time is not yet set, calculates from start to now
     */
    private calculateElapsedTime(): void {
        const startTs = this.completeBatchForm?.get('start_ts')?.value;
        const endTs = this.completeBatchForm?.get('end_ts')?.value;

        if (!startTs) {
            this.elapsedTime = '--';
            return;
        }

        const startTime = new Date(startTs);
        // Use end time if set, otherwise use current time
        const endTime = endTs ? new Date(endTs) : new Date();
        const diffMs = endTime.getTime() - startTime.getTime();

        if (diffMs < 0) {
            this.elapsedTime = 'Invalid (end before start)';
            return;
        }

        const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

        if (days > 0) {
            this.elapsedTime = `${days}d ${hours}h ${minutes}m`;
        } else if (hours > 0) {
            this.elapsedTime = `${hours}h ${minutes}m`;
        } else {
            this.elapsedTime = `${minutes}m`;
        }
    }

    /**
     * Handle form submission
     * Calls the appropriate API internally based on context:
     * - If batchRunId provided (Process Flow): updateBatch + completeBatchRun
     * - Otherwise (OEE Dashboard): completeBatch
     */
    onSubmit(): void {
        // Mark all fields as touched to show validation errors
        this.completeBatchForm.markAllAsTouched();

        if (this.completeBatchForm.invalid) {
            return;
        }

        const formValue = this.completeBatchForm.value;

        // Build custom field values - filter out empty values
        const customFieldValues = this.buildCustomFieldValues(formValue.custom_field_values);

        this.submitting = true;

        // Process Flow path: batchRunId is provided
        if (this.data.batchRunId) {
            this.completeViaProcessFlow(formValue, customFieldValues);
        } else {
            // OEE Dashboard path: use completeBatch endpoint
            this.completeViaDashboard(formValue, customFieldValues);
        }
    }

    /**
     * Complete batch via OEE Dashboard path (single completeBatch call)
     */
    private completeViaDashboard(formValue: Record<string, string | number | undefined>, customFieldValues: CustomFieldValueMap): void {
        const request: CompleteBatchRequest = {
            batch_id: this.selectedBatch.batch_id,
            result: 'completed',
            good_parts: Number(formValue.good_parts),
            reject_parts: Number(formValue.reject_parts)
        };

        // Include timestamps - convert to ISO strings
        if (formValue.start_ts) {
            request.start_ts = new Date(formValue.start_ts).toISOString();
        }
        if (formValue.end_ts) {
            request.end_ts = new Date(formValue.end_ts).toISOString();
        }

        // Include batch_number if it was changed
        if (formValue.batch_number !== this.selectedBatch.batch_number) {
            request.batch_number = String(formValue.batch_number);
        }

        // Include custom field values
        if (Object.keys(customFieldValues).length > 0) {
            request.custom_field_values = customFieldValues;
        }

        this.oeeDataService.completeBatch(request).subscribe({
            next: () => {
                this.snackBar.open('Job completed successfully', 'Dismiss', { duration: 3000 });
                this.dialogRef.close({ success: true });
            },
            error: (err) => {
                console.error('Failed to complete batch:', err);
                this.submitting = false;
                this.snackBar.open('Failed to complete job. Please try again.', 'Close', { duration: 5000 });
            }
        });
    }

    /**
     * Complete batch via Process Flow path (updateBatch + completeBatchRun)
     */
    private completeViaProcessFlow(formValue: Record<string, string | number | undefined>, customFieldValues: CustomFieldValueMap): void {
        // Convert timestamps to ISO strings
        const startTs = formValue.start_ts ? new Date(formValue.start_ts).toISOString() : undefined;
        const endTs = formValue.end_ts ? new Date(formValue.end_ts).toISOString() : new Date().toISOString();

        // First update the batch with parts data and timestamps
        const updateRequest = {
            batch_id: this.selectedBatch.batch_id,
            good_parts: Number(formValue.good_parts),
            reject_parts: Number(formValue.reject_parts),
            batch_number: formValue.batch_number !== this.selectedBatch.batch_number ? String(formValue.batch_number) : undefined,
            start_ts: startTs,
            end_ts: endTs
        };

        // Update batch first, then complete the batch run
        this.oeeDataService.updateBatch(updateRequest).pipe(
            switchMap(() => {
                // Complete the batch run with custom field values and the user-provided end time
                return this.schematicService.completeBatchRun({
                    run_id: this.data.batchRunId!,
                    end_ts: endTs,
                    custom_field_values: Object.keys(customFieldValues).length > 0 ? customFieldValues : undefined
                });
            })
        ).subscribe({
            next: () => {
                this.snackBar.open('Job completed successfully', 'Dismiss', { duration: 3000 });
                this.dialogRef.close({ success: true });
            },
            error: (err) => {
                console.error('Failed to complete batch:', err);
                this.submitting = false;
                this.snackBar.open('Failed to complete job. Please try again.', 'Close', { duration: 5000 });
            }
        });
    }

    /**
     * Build custom field values object, filtering out empty values
     */
    private buildCustomFieldValues(values: { [key: string]: CustomFieldValue }): CustomFieldValueMap {
        const result: CustomFieldValueMap = {};
        
        if (!values) return result;

        Object.entries(values).forEach(([fieldId, value]) => {
            // Include value if it's not null/undefined and not an empty string
            // Also include false boolean values (for checkboxes)
            if (value !== null && value !== undefined && value !== '') {
                result[fieldId] = value;
            } else if (value === false) {
                result[fieldId] = value;
            }
        });

        return result;
    }

    /** Footer actions rendered by `<app-detail-action-bar flavor="material">`. */
    get footerActions(): DetailHeaderAction[] {
        return [
            {
                id: 'cancel',
                label: 'Cancel',
                tone: 'secondary',
                disabled: this.submitting,
                align: 'start',
                testId: 'btn-cancel'
            },
            {
                id: 'abort',
                label: 'Abort Instead',
                matIcon: 'cancel',
                tone: 'warning',
                disabled: this.submitting,
                testId: 'btn-abort'
            },
            {
                id: 'submit',
                label: this.completeBatchLabel,
                matIcon: 'check_circle',
                tone: 'success',
                loading: this.submitting,
                disabled: this.submitting || !this.canSubmit,
                disabledReason: !this.canSubmit ? 'Complete all required fields to continue' : '',
                testId: 'btn-confirm'
            }
        ]
    }

    onFooterAction(a: DetailHeaderAction): void {
        if (a.id === 'cancel') this.onCancel()
        else if (a.id === 'abort') this.openAbortModal()
        else if (a.id === 'submit') this.onSubmit()
    }

    /**
     * Handle cancel
     */
    onCancel(): void {
        this.dialogRef.close();
    }

    /**
     * Open abort batch modal instead of completing
     * Closes this modal and opens the abort modal
     */
    openAbortModal(): void {
        const batch = this.selectedBatch || this.data.batch;

        const abortDialogRef = this.dialog.open(AbortBatchModalComponent, {
            width: '550px',
            panelClass: 'oee-mobile-modal',
            disableClose: false,
            data: {
                batch: batch,
                equipmentName: this.data.equipmentName,
                equipmentId: this.data.equipmentId
            } as AbortBatchModalData
        });

        abortDialogRef.afterClosed().subscribe((result: AbortBatchModalResult | undefined) => {
            if (result?.aborted) {
                // Close this modal and pass the abort result to the parent
                this.dialogRef.close({ aborted: true, ...result });
            }
            // If not aborted, user cancelled - keep this modal open
        });
    }

    /**
     * Get total parts
     */
    getTotalParts(): number {
        const good = this.completeBatchForm.get('good_parts')?.value || 0;
        const reject = this.completeBatchForm.get('reject_parts')?.value || 0;
        return good + reject;
    }
}
